import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import './ManageContracts.linear.css';

// Linear palette literals for the handful of JS-side color needs (AsyncSelect
// styles object + inline SVG/legend fills). The bulk of styling is CSS-class
// based and lives in ManageContracts.linear.css. Values are the mockup's light
// :root (docs/design/mockups/contracts-linear.html) -- kept in sync with the
// CSS token block so a future dark-mode lane moves both together.
const L = {
  text: '#16171b', text2: '#5f636d', text3: '#8d919b',
  accent: '#3a53df', good: '#1f9d55', warn: '#bd7d1a', crit: '#d0433a',
  border: '#e9eaee', panel2: '#f7f8fa',
  goodSoft: 'rgba(31,157,85,.10)', warnSoft: 'rgba(189,125,26,.12)',
};

// Debounce helper for AsyncSelect's callback-based loadOptions.
// react-select expects loadOptions(inputValue, callback); the callback form
// avoids the Promise-debounce hanging-callback footgun.
function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

const CLAUSE_LABELS = {
  termination: 'Termination',
  exclusivity: 'Exclusivity',
  penalty: 'Penalty',
  warranty: 'Warranty',
  liability_cap: 'Liability Cap',
  force_majeure: 'Force Majeure',
  price_escalation: 'Price Escalation',
};

// ── small inline icons (stroke=currentColor so they inherit the button color) ──
const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
);
const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
);
const IconKebab = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
);

// ── page-scoped Linear modal chrome (overlay + dialog + header). NOT a shared
//    component -- lives in this page file per the Lane 209 page-scoped rebuild.
//    Backdrop-click and Escape close; the dialog body/footer are provided by the
//    caller so each modal can compose its own sections. ──
function LinearModal({ onClose, size, title, subtitle, headerRight, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="ct-overlay" onClick={onClose}>
      <div className={`ct-modal ${size || ''}`} role="dialog" aria-modal="true" aria-label={title}
        onClick={(e) => e.stopPropagation()}>
        <div className="ct-mh">
          <div style={{ minWidth: 0 }}>
            <div className="mt">{title}</div>
            {subtitle && <div className="msub">{subtitle}</div>}
          </div>
          {headerRight}
          <button className="x" aria-label="Close" onClick={onClose}><IconClose /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ManageContracts() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [message, setMessage] = useState(null);

  // Upload new contract
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null, contract_number: '', contract_name: '',
    supplier_name: '', effective_date: '', expiration_date: '', contract_value: '',
  });

  // Edit contract
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [editForm, setEditForm] = useState({
    effective_date: '', expiration_date: '', contract_name: '', status: '',
  });
  const [saving, setSaving] = useState(false);

  // Parse state
  const [parsing, setParsing] = useState(null);
  const [showParseModal, setShowParseModal] = useState(false);
  const [parseData, setParseData] = useState(null);
  const [parseContract, setParseContract] = useState(null);
  const [parseMaterials, setParseMaterials] = useState([]);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [parseReviewing, setParseReviewing] = useState(false);
  const [showExtractedText, setShowExtractedText] = useState(false);

  // Upload to existing contract
  const [uploadTargetId, setUploadTargetId] = useState(null);

  // Overflow menu
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0, openUp: false });

  useEffect(() => { fetchContracts(); }, [token]); // eslint-disable-line

  // Close overflow menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchContracts = async () => {
    try {
      setFetchError(null);
      const response = await fetch(`${API_BASE}/api/company/contracts?page_size=200`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data.contracts || []);
      } else {
        setFetchError('Unable to load contracts. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setFetchError('Unable to load contracts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────
  const openEditModal = (contract) => {
    setEditContract(contract);
    setEditForm({
      effective_date: contract.effective_date ? contract.effective_date.split('T')[0] : '',
      expiration_date: contract.expiration_date ? contract.expiration_date.split('T')[0] : '',
      contract_name: contract.contract_name || '',
      status: contract.status || 'active',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editContract) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/contracts/${editContract.contract_id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({
          effective_date: editForm.effective_date || null,
          expiration_date: editForm.expiration_date || null,
          contract_name: editForm.contract_name || null,
          status: editForm.status || null,
        })
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Contract updated.' });
        setShowEditModal(false); setEditContract(null); fetchContracts();
      } else {
        setMessage({ type: 'error', text: 'Failed to update contract.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating contract.' });
    } finally { setSaving(false); }
  };

  // ── Upload new contract ───────────────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.supplier_name) {
      setMessage({ type: 'error', text: 'Supplier name is required.' });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    Object.entries(uploadForm).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });
    try {
      const response = await fetch(`${API_BASE}/api/contracts/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setShowUploadModal(false);
        resetForm();
        fetchContracts();
        if (data.parse_result && data.parse_result.success) {
          setMessage({ type: 'success', text: 'Contract uploaded and parsed successfully.' });
          openParseReview(data.contract_id);
        } else {
          setMessage({ type: 'success', text: 'Contract uploaded. AI parsing terms automatically.' });
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Upload failed.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed: ' + error.message });
    } finally { setUploading(false); }
  };

  // ── Upload document to existing contract ──────────────────────────
  const handleUploadToContract = (contractId) => {
    setUploadTargetId(contractId);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTargetId) return;
    setParsing(uploadTargetId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_BASE}/api/contracts/${uploadTargetId}/upload-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: 'Contract uploaded. AI parsing terms automatically.' });
        fetchContracts();
        if (data.parse_result && data.parse_result.success) {
          openParseReview(uploadTargetId);
        }
      } else {
        const errData = await response.json();
        setMessage({ type: 'error', text: errData.detail || 'Upload failed.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed: ' + error.message });
    } finally {
      setParsing(null);
      setUploadTargetId(null);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const handleDelete = async (contractId, contractNumber) => {
    if (!window.confirm(`Delete contract ${contractNumber}?`)) return;
    try {
      const response = await fetch(`${API_BASE}/api/contracts/${contractId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Contract deleted.' });
        fetchContracts();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Delete failed.' });
    }
  };

  // ── Parse ─────────────────────────────────────────────────────────
  const handleParse = async (contractId) => {
    setParsing(contractId);
    try {
      const response = await fetch(`${API_BASE}/api/contracts/${contractId}/parse`, {
        method: 'POST', headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Contract parsed successfully.' });
        openParseReview(contractId);
        fetchContracts();
      } else {
        setMessage({ type: 'error', text: data.detail || data.message || 'Parsing failed.' });
        fetchContracts();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Parsing failed: ' + error.message });
    } finally { setParsing(null); }
  };

  const openParseReview = async (contractId) => {
    const row = contracts.find(c => c.contract_id === contractId);
    setParseContract(row || null);
    try {
      const response = await fetch(`${API_BASE}/api/contracts/${contractId}/parsed`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.parsed) {
        setParseData(data.terms);
        setParseMaterials(data.materials || []);
        setAvailableMaterials(data.available_materials || []);
        setShowExtractedText(false);
        setShowParseModal(true);
      } else {
        setMessage({ type: 'error', text: 'No parsed data found for this contract.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load parsed data.' });
    }
  };

  const handleAcceptParsed = async (applyToContract) => {
    if (!parseData) return;
    setParseReviewing(true);
    try {
      const matCorrections = parseMaterials.map(m => ({
        parsed_material_id: m.parsed_material_id,
        matched_material_number: m.matched_material_number,
        is_confirmed: m.matched_material_number ? true : false,
      }));
      const response = await fetch(
        `${API_BASE}/api/contracts/${parseData.contract_id}/parsed/review`,
        {
          method: 'PUT', headers: getAuthHeaders(),
          body: JSON.stringify({
            action: 'accept',
            corrections: { materials: matCorrections },
            apply_to_contract: applyToContract,
          })
        }
      );
      if (response.ok) {
        setMessage({
          type: 'success',
          text: applyToContract
            ? 'Parsed terms accepted and applied to contract.'
            : 'Parsed terms reviewed and saved.'
        });
        setShowParseModal(false);
        setParseData(null);
        fetchContracts();
      } else {
        setMessage({ type: 'error', text: 'Failed to save review.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Review failed: ' + error.message });
    } finally { setParseReviewing(false); }
  };

  const updateMaterialMatch = (idx, materialNumber) => {
    setParseMaterials(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], matched_material_number: materialNumber, match_method: 'manual' };
      return updated;
    });
  };

  // AsyncSelect loadOptions for the matched-material dropdown — closes Bug C
  // (§5.BP, 2026-06-17). Server-side filtered via /api/contracts/materials/search;
  // 250ms debounce wraps the callback form to avoid the Promise-debounce
  // hanging-callback footgun. availableMaterials seed (top-50 from the parsed
  // response) populates defaultOptions so the dropdown is responsive on open
  // without an extra round-trip.
  //
  // `limit=50` mirrors backend MATERIALS_AUTOCOMPLETE_DEFAULT_LIMIT in
  // backend/routes/contracts.py — keep these in lockstep if either changes.
  const loadMaterialOptions = useCallback((inputValue, callback) => {
    fetch(`${API_BASE}/api/contracts/materials/search?q=${encodeURIComponent(inputValue || '')}&limit=50`, {
      headers: getAuthHeaders()
    })
      .then(r => r.ok ? r.json() : { results: [] })
      .then(data => callback((data.results || []).map(m => ({
        value: m.material_number,
        label: `${m.material_number} - ${m.material_name}`,
      }))))
      .catch(() => callback([]));
  }, [getAuthHeaders]);

  const debouncedLoadMaterialOptions = useMemo(
    () => debounce(loadMaterialOptions, 250),
    [loadMaterialOptions]
  );

  // Label-lookup fallback for the AsyncSelect value. Pre-existing matched
  // materials may not be in the top-50 alphabetical seed (the common case at
  // XMEK 100K-material scale); when missing, we render the bare material_number.
  // See §5.BX for the LEFT JOIN materials_master follow-up that would render
  // "<number> - <name>" for outside-top-50 matches.
  const matLabelFor = useCallback((matNumber) => {
    if (!matNumber) return null;
    const found = availableMaterials.find(am => am.material_number === matNumber);
    return found ? `${matNumber} - ${found.material_name}` : matNumber;
  }, [availableMaterials]);

  const defaultMatOptions = useMemo(
    () => availableMaterials.map(am => ({
      value: am.material_number,
      label: `${am.material_number} - ${am.material_name}`,
    })),
    [availableMaterials]
  );

  const asyncSelectStyles = useCallback((hasValue) => ({
    container: (base) => ({ ...base, width: '100%' }),
    control: (base) => ({
      ...base,
      minHeight: '32px',
      fontSize: '12px',
      backgroundColor: hasValue ? L.goodSoft : L.warnSoft,
      borderColor: L.border,
      borderRadius: '7px',
      boxShadow: 'none',
    }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: '12px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base) => ({ ...base, fontSize: '12px' }),
    singleValue: (base) => ({ ...base, fontSize: '12px' }),
    input: (base) => ({ ...base, fontSize: '12px' }),
    placeholder: (base) => ({ ...base, fontSize: '12px', color: L.text3 }),
  }), []);

  const resetForm = () => {
    setUploadForm({
      file: null, contract_number: '', contract_name: '',
      supplier_name: '', effective_date: '', expiration_date: '', contract_value: '',
    });
  };

  // ── Formatters ────────────────────────────────────────────────────
  const formatFieldName = (field) => {
    if (!field || typeof field !== 'string') return field || '-';
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str || '-';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const year = d.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  };

  const formatDateFull = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ── Row badges (lifecycle + parse status) ─────────────────────────
  const lifecycleBadge = (row) => {
    const days = row.days_until_expiry;
    if (days != null && days <= 0) return <span className="ct-life exp">Expired</span>;
    if (days != null && days > 0 && days <= 90) return <span className="ct-life soon">Expiring Soon</span>;
    if (row.status?.toLowerCase() === 'active') return <span className="ct-life act">Active</span>;
    if (row.status?.toLowerCase() === 'expired') return <span className="ct-life exp">Expired</span>;
    return <span className="ct-life mut">{row.status || 'Unknown'}</span>;
  };

  const parseBadge = (ps) => {
    if (ps === 'reviewed') return <span className="ct-prs done">Parsed</span>;
    if (ps === 'completed') return <span className="ct-prs warn">Needs Review</span>;
    if (ps === 'pending' || ps === 'parsing') return <span className="ct-prs run"><span className="sp" />Parsing</span>;
    if (ps === 'failed') return <span className="ct-prs fail">Failed</span>;
    return <span className="ct-prs no">Not Parsed</span>;
  };

  // ── Filter ────────────────────────────────────────────────────────
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = !searchQuery ||
      contract.contract_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.contract_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || (() => {
      if (statusFilter === 'expiring') {
        return contract.days_until_expiry != null && contract.days_until_expiry > 0 && contract.days_until_expiry <= 90;
      }
      return contract.status?.toLowerCase() === statusFilter.toLowerCase();
    })();
    return matchesSearch && matchesStatus;
  });

  // ── Stats ─────────────────────────────────────────────────────────
  const activeContracts = contracts.filter(c => c.status?.toLowerCase() === 'active').length;
  const expiringContracts = contracts.filter(c => c.days_until_expiry != null && c.days_until_expiry > 0 && c.days_until_expiry <= 90).length;
  const parsedContracts = contracts.filter(c => c.parse_status === 'completed' || c.parse_status === 'reviewed').length;

  // ── Renewal calendar (the one added visual) ───────────────────────
  // Plots each contract that has an expiration date on a time axis, colored by
  // lifecycle (expired / expiring ≤90d / active). Derived entirely from existing
  // days_until_expiry / expiration_date data — no new metric. Honest empty state
  // when no contract carries an end date.
  const renderTimeline = () => {
    if (contracts.length === 0) return null;
    const VB_W = 1072, PLOT_L = 40, PLOT_R = 1032, BASE_Y = 78, DOT_Y = 58, BAND_TOP = 20;
    const DAY = 86400000;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const datable = contracts
      .filter(c => c.expiration_date)
      .map(c => {
        const exp = new Date(c.expiration_date);
        const days = c.days_until_expiry;
        const dd = days != null ? days : Math.round((exp - today) / DAY);
        const cls = dd <= 0 ? 'exp' : dd <= 90 ? 'soon' : 'act';
        return { c, exp, cls };
      })
      .filter(d => !isNaN(d.exp.getTime()));

    const legend = (
      <span className="leg">
        <span><i style={{ background: L.crit }} />Expired</span>
        <span><i style={{ background: L.warn }} />Expiring &le;90d</span>
        <span><i style={{ background: L.good }} />Active</span>
      </span>
    );

    let body;
    if (datable.length === 0) {
      body = <div className="ct-tl-empty">No contract end dates on file yet. Add expiry dates to see the renewal calendar.</div>;
    } else {
      const times = datable.map(d => d.exp.getTime()).concat(today.getTime());
      let min = Math.min(...times), max = Math.max(...times);
      const SIX_MO = 182 * DAY;
      if (max - min < SIX_MO) { const mid = (min + max) / 2; min = mid - SIX_MO / 2; max = mid + SIX_MO / 2; }
      const pad = (max - min) * 0.04; min -= pad; max += pad;
      const span = max - min || 1;
      const x = (t) => PLOT_L + ((t - min) / span) * (PLOT_R - PLOT_L);
      const xToday = x(today.getTime());
      const x90 = Math.min(x(today.getTime() + 90 * DAY), PLOT_R);

      const ticks = [];
      let ty = new Date(min).getFullYear(), tq = Math.floor(new Date(min).getMonth() / 3);
      let td = new Date(ty, tq * 3, 1);
      while (td.getTime() <= max && ticks.length < 40) {
        if (td.getTime() >= min) ticks.push(new Date(td));
        tq += 1; if (tq > 3) { tq = 0; ty += 1; }
        td = new Date(ty, tq * 3, 1);
      }
      const labelEvery = ticks.length > 12 ? 2 : 1;

      body = (
        <svg viewBox={`0 0 ${VB_W} 118`} role="img" aria-label="Contracts plotted by expiry date">
          {x90 > xToday && <rect className="ct-tlband" x={xToday} y={BAND_TOP} width={x90 - xToday} height={BASE_Y - BAND_TOP} />}
          {ticks.map((t, i) => {
            const tx = x(t.getTime());
            return (
              <g key={`tick-${i}`}>
                <line className="ct-tlgrid" x1={tx} y1={BAND_TOP} x2={tx} y2={BASE_Y} />
                {i % labelEvery === 0 && (
                  <text className="ct-tlax" x={tx} y={96} textAnchor="middle">
                    {`Q${Math.floor(t.getMonth() / 3) + 1} '${String(t.getFullYear()).slice(-2)}`}
                  </text>
                )}
              </g>
            );
          })}
          <line className="ct-tltoday" x1={xToday} y1={14} x2={xToday} y2={82} />
          <text className="ct-tllab" x={Math.min(xToday + 4, PLOT_R - 78)} y={13}>
            {`Today · ${today.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
          </text>
          <line className="ct-tlbase" x1={PLOT_L} y1={BASE_Y} x2={PLOT_R} y2={BASE_Y} />
          {datable.map((d, i) => {
            const cx = x(d.exp.getTime());
            const tip = `${d.c.supplier_name || 'Not available'} · ${d.c.contract_number || ''} · Expires ${d.exp.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
            if (d.cls === 'soon') {
              return (
                <g key={`dot-${i}`}>
                  <title>{tip}</title>
                  <circle className="ct-dot-ring" cx={cx} cy={DOT_Y} r="7" />
                  <circle className="ct-dot-soon" cx={cx} cy={DOT_Y} r="5" />
                </g>
              );
            }
            return (
              <circle key={`dot-${i}`} className={d.cls === 'exp' ? 'ct-dot-exp' : 'ct-dot-act'} cx={cx} cy={DOT_Y} r="5">
                <title>{tip}</title>
              </circle>
            );
          })}
        </svg>
      );
    }

    return (
      <section className="ct-tl-card" aria-label="Renewal calendar">
        <div className="ct-tl-h"><span className="t">Renewal calendar</span>{legend}</div>
        {body}
      </section>
    );
  };

  const clauses = parseData ? (() => {
    const c = parseData.clauses;
    if (!c) return {};
    if (typeof c === 'string') { try { return JSON.parse(c); } catch { return {}; } }
    return c;
  })() : {};

  /** Safely parse and render volume_commitments — handles JSON strings, objects, arrays, and plain text */
  const renderVolumeCommitments = (raw) => {
    const muted = { fontStyle: 'italic', color: L.text3 };
    if (!raw) return <span style={muted}>None specified</span>;
    let data = raw;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { /* plain string — render below */ }
    }
    if (Array.isArray(data)) {
      if (data.length === 0) return <span style={muted}>None specified</span>;
      return (
        <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
          {data.map((item, i) => (
            <li key={i} style={{ fontSize: '12.5px', color: L.text2, lineHeight: '1.6' }}>
              {typeof item === 'object'
                ? `${item.material || item.material_name || item.description || 'Material'}: ${
                    Number(item.volume || item.quantity || item.annual_volume || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })
                  } ${item.unit || item.unit_of_measure || 'units'}/year`
                : String(item)}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof data === 'object' && data !== null) {
      const entries = Object.entries(data);
      if (entries.length === 0) return <span style={muted}>None specified</span>;
      return (
        <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
          {entries.map(([key, val]) => (
            <li key={key} style={{ fontSize: '12.5px', color: L.text2, lineHeight: '1.6' }}>
              {formatFieldName(key)}: {typeof val === 'number' ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : String(val)}
            </li>
          ))}
        </ul>
      );
    }
    return <span style={{ fontSize: '12.5px', color: L.text2, lineHeight: '1.5' }}>{String(data)}</span>;
  };

  /** Render any field that may be raw JSON — parses it to clean text */
  const renderSmartField = (raw) => {
    if (!raw) return '-';
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
          if (Array.isArray(parsed)) return parsed.join(', ');
          return Object.entries(parsed)
            .map(([k, v]) => `${formatFieldName(k)}: ${v}`)
            .join(', ');
        }
        return String(parsed);
      } catch { return raw; }
    }
    if (typeof raw === 'object') return JSON.stringify(raw);
    return String(raw);
  };

  const calcNoticeDeadline = () => {
    if (!parseData?.extracted_expiration_date || !parseData?.renewal_notice_days) return null;
    const exp = new Date(parseData.extracted_expiration_date);
    exp.setDate(exp.getDate() - parseData.renewal_notice_days);
    return formatDateFull(exp);
  };

  // ── Loading / hard-error states ───────────────────────────────────
  if (loading) {
    return (
      <div className="ct">
        <div className="ct-topbar"><div className="ct-crumb"><span className="cur">Contracts</span></div></div>
        <div className="ct-wrap"><div className="ct-loading">Loading contracts…</div></div>
      </div>
    );
  }

  if (fetchError && contracts.length === 0) {
    return (
      <div className="ct">
        <div className="ct-topbar"><div className="ct-crumb"><span className="cur">Contracts</span></div></div>
        <div className="ct-wrap">
          <header className="ct-head">
            <div><h1 className="ct-h1">Contracts</h1><div className="ct-sub">Manage your supplier contracts</div></div>
          </header>
          <div className="ct-empty">
            <div className="et">Unable to load contracts</div>
            <div className="ed">{fetchError}</div>
            <button className="ct-btn pri" onClick={fetchContracts}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const confPct = parseData && parseData.confidence_score != null ? Math.round(parseData.confidence_score * 100) : null;
  const confColor = confPct != null ? (confPct >= 80 ? L.good : confPct >= 50 ? L.warn : L.crit) : L.text3;
  const clausesFound = Object.keys(CLAUSE_LABELS).filter(k => {
    const cl = clauses[k];
    return cl && (cl.summary || cl.is_exclusive != null);
  }).length;

  return (
    <div className="ct">
      {/* Hidden file input for upload-to-existing */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf" onChange={handleFileSelected} />

      {/* top bar / breadcrumb */}
      <div className="ct-topbar"><div className="ct-crumb"><span className="cur">Contracts</span></div></div>

      <div className="ct-wrap">
        {/* header */}
        <header className="ct-head">
          <div>
            <h1 className="ct-h1">Contracts</h1>
            <div className="ct-sub">Upload agreements, let AI read the terms, review them, and track what&rsquo;s expiring</div>
          </div>
          <button className="ct-btn pri ct-headbtn" onClick={() => setShowUploadModal(true)}>
            <IconPlus />Add contract
          </button>
        </header>

        {/* toast */}
        {message && (
          <div className={`ct-toast ${message.type === 'success' ? 'ok' : 'err'}`}>
            <span className="msg">{message.text}</span>
            <button onClick={() => setMessage(null)} aria-label="Dismiss">&times;</button>
          </div>
        )}

        {/* figure strip (computed stat line) */}
        <section className="ct-figs" aria-label="Contract summary">
          <div className="ct-fig"><div className="k">Contracts</div><div className="v">{contracts.length}</div><div className="d">on file</div></div>
          <div className="ct-fig"><div className="k">Active</div><div className="v good">{activeContracts}</div><div className="d">in force</div></div>
          <div className="ct-fig"><div className="k">Expiring soon</div><div className={`v ${expiringContracts > 0 ? 'warn' : ''}`}>{expiringContracts}</div><div className="d">within 90 days</div></div>
          <div className="ct-fig"><div className="k">Parsed</div><div className="v">{parsedContracts}</div><div className="d">terms extracted &amp; reviewable</div></div>
        </section>

        {/* renewal calendar */}
        {renderTimeline()}

        {/* filters */}
        <div className="ct-fbar">
          <div className="ct-search">
            <IconSearch />
            <input placeholder="Search contract # or supplier" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} aria-label="Search contracts" />
          </div>
          <div className="ct-seg" role="group" aria-label="Filter by status">
            {[['', 'All'], ['active', 'Active'], ['expiring', 'Expiring'], ['expired', 'Expired']].map(([val, lab]) => (
              <button key={lab} className={statusFilter === val ? 'on' : ''} onClick={() => setStatusFilter(val)}>{lab}</button>
            ))}
          </div>
          {(searchQuery || statusFilter) && (
            <button className="ct-btn ghost" onClick={() => { setSearchQuery(''); setStatusFilter(''); }}>Clear filters</button>
          )}
          <span className="rt">{filteredContracts.length} {filteredContracts.length === 1 ? 'contract' : 'contracts'}</span>
        </div>

        {/* table / empty */}
        {filteredContracts.length === 0 ? (
          <div className="ct-empty">
            <div className="et">No contracts found</div>
            <div className="ed">{searchQuery || statusFilter ? 'Try adjusting your search or filters' : 'Upload your first contract to get started'}</div>
            <button className="ct-btn pri" onClick={() => setShowUploadModal(true)}>Add contract</button>
          </div>
        ) : (
          <>
            <section className="ct-tbl" aria-label="Contracts">
              <div className="ct-row hd">
                <span>Contract&nbsp;#</span><span>Supplier</span><span style={{ textAlign: 'right' }}>Materials</span>
                <span>Start</span><span>End</span><span>Status</span><span style={{ textAlign: 'right' }} />
              </div>
              {filteredContracts.map((row) => {
                const ps = row.parse_status;
                const reviewable = ps === 'completed' || ps === 'reviewed';
                const rowOpen = () => { if (reviewable) openParseReview(row.contract_id); };
                let endTip = row.expiration_date ? formatDateFull(row.expiration_date) : '';
                if (row.days_until_expiry != null && row.days_until_expiry > 0) endTip += ` (${row.days_until_expiry} days)`;
                return (
                  <div key={row.contract_id} className={`ct-row${reviewable ? ' click' : ''}`}
                    {...(reviewable ? {
                      role: 'button', tabIndex: 0, onClick: rowOpen,
                      onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); rowOpen(); } },
                    } : {})}>
                    <div className="ct-cno" title={row.contract_number || ''}>{row.contract_number}</div>
                    <div className="ct-sup" title={row.supplier_name || ''}>{row.supplier_name}</div>
                    <div className={`ct-num${row.material_count > 0 ? '' : ' none'}`}>{row.material_count > 0 ? row.material_count : 'None'}</div>
                    <div className="ct-dt" title={row.effective_date ? formatDateFull(row.effective_date) : ''}>{formatDate(row.effective_date)}</div>
                    <div className="ct-dt" title={endTip}>{formatDate(row.expiration_date)}</div>
                    <div className="ct-stat">{lifecycleBadge(row)}{parseBadge(ps)}</div>
                    <div className="ct-actcell" onClick={(e) => e.stopPropagation()}>
                      {reviewable ? (
                        <button className="ct-lnk" onClick={() => openParseReview(row.contract_id)}>Review</button>
                      ) : ps === 'failed' ? (
                        <button className="ct-lnk warn" disabled={parsing === row.contract_id} onClick={() => handleParse(row.contract_id)}>
                          {parsing === row.contract_id ? 'Parsing…' : 'Retry parse'}
                        </button>
                      ) : (ps === 'pending' || ps === 'parsing') ? (
                        <span className="ct-parsing-inline"><span className="sp" style={{ width: 9, height: 9, border: '1.5px solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'ctspin .7s linear infinite' }} />Parsing</span>
                      ) : !row.file_path ? (
                        <button className="ct-lnk" disabled={parsing === row.contract_id} onClick={() => handleUploadToContract(row.contract_id)}>Upload</button>
                      ) : (
                        <button className="ct-lnk" disabled={parsing === row.contract_id} onClick={() => handleParse(row.contract_id)}>
                          {parsing === row.contract_id ? 'Parsing…' : 'Parse'}
                        </button>
                      )}
                      <button className="ct-kebab" aria-label="More actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openMenuId === row.contract_id) { setOpenMenuId(null); return; }
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 84;
                          const openUp = rect.bottom + menuHeight + 8 > window.innerHeight;
                          setMenuPos({
                            top: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4,
                            right: window.innerWidth - rect.right,
                            openUp,
                          });
                          setOpenMenuId(row.contract_id);
                        }}>
                        <IconKebab />
                      </button>
                      {openMenuId === row.contract_id && (
                        <div className="ct-menu" style={{ top: menuPos.top, right: menuPos.right }}>
                          <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); openEditModal(row); }}>Edit</button>
                          <button className="del" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleDelete(row.contract_id, row.contract_number); }}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>

            <div className="ct-pg"><span>Showing <b>{filteredContracts.length}</b> of <b>{contracts.length}</b> contracts</span></div>
          </>
        )}

        {/* footnote — color semantics + parse honesty */}
        <div className="ct-foot-note">
          Expiry color is consistent across the summary, the renewal calendar, the filter and the badge:{' '}
          <b className="crit">Expired</b> (&le; 0 days) &middot; <b className="warn">Expiring</b> (&le; 90 days) &middot; <b className="good">Active</b>.
          The second badge is parse state (Parsed / Needs Review / Parsing / Not Parsed), separate from lifecycle.
          AI parsing never invents a term. An unfound field stays empty and a non-contract upload is rejected, not fabricated.
        </div>
      </div>

      {/* ── Upload New Contract Modal ── */}
      {showUploadModal && (
        <LinearModal size="sm" title="Add contract" subtitle="Upload a PDF. AI reads the terms after upload"
          onClose={() => { setShowUploadModal(false); resetForm(); }}>
          <form onSubmit={handleUpload}>
            <div className="ct-mbody">
              <div className="ct-msec" style={{ borderBottom: 0 }}>
                <div className="ct-field" style={{ marginTop: 0 }}>
                  <div className="k">Contract document</div>
                  <div className="ct-drop">
                    <input type="file" accept=".pdf" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })} />
                    <div className="ct-hint">PDF only &middot; contracts must be uploaded as PDF files</div>
                  </div>
                </div>
                <div className="ct-grid2">
                  <div className="ct-field"><div className="k">Contract number</div>
                    <input className="ct-input" value={uploadForm.contract_number}
                      onChange={(e) => setUploadForm({ ...uploadForm, contract_number: e.target.value })} placeholder="Auto-generated if blank" /></div>
                  <div className="ct-field"><div className="k">Contract name</div>
                    <input className="ct-input" value={uploadForm.contract_name}
                      onChange={(e) => setUploadForm({ ...uploadForm, contract_name: e.target.value })} placeholder="e.g., Metals Supply Agreement" /></div>
                </div>
                <div className="ct-field"><div className="k">Supplier <span className="ct-req">*</span></div>
                  <input className="ct-input" value={uploadForm.supplier_name}
                    onChange={(e) => setUploadForm({ ...uploadForm, supplier_name: e.target.value })} placeholder="Supplier name" /></div>
                <div className="ct-grid3">
                  <div className="ct-field"><div className="k">Effective date</div>
                    <input className="ct-input" type="date" value={uploadForm.effective_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, effective_date: e.target.value })} /></div>
                  <div className="ct-field"><div className="k">Expiration date</div>
                    <input className="ct-input" type="date" value={uploadForm.expiration_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, expiration_date: e.target.value })} /></div>
                  <div className="ct-field"><div className="k">Annual value</div>
                    <input className="ct-input" type="number" value={uploadForm.contract_value}
                      onChange={(e) => setUploadForm({ ...uploadForm, contract_value: e.target.value })} placeholder="$0" /></div>
                </div>
              </div>
            </div>
            <div className="ct-mfoot">
              <div className="ct-ainote" style={{ background: 'transparent', borderLeft: 0, padding: 0, color: L.text3 }}>
                Parsing runs in the background after upload. You&rsquo;ll see a <b style={{ color: L.text2 }}>Parsing</b> badge, then <b style={{ color: L.text2 }}>Review</b> when it&rsquo;s ready.
              </div>
              <div className="rt">
                <button type="button" className="ct-btn" onClick={() => { setShowUploadModal(false); resetForm(); }}>Cancel</button>
                <button type="submit" className="ct-btn pri" disabled={uploading}>{uploading ? 'Uploading…' : 'Add contract'}</button>
              </div>
            </div>
          </form>
        </LinearModal>
      )}

      {/* ── Edit Contract Modal ── */}
      {showEditModal && editContract && (
        <LinearModal size="sm" title={`Edit: ${editContract.contract_number}`}
          onClose={() => { setShowEditModal(false); setEditContract(null); }}>
          <div className="ct-mbody">
            <div className="ct-msec" style={{ borderBottom: 0 }}>
              <div className="ct-field" style={{ marginTop: 0 }}><div className="k">Contract name</div>
                <input className="ct-input" value={editForm.contract_name}
                  onChange={(e) => setEditForm({ ...editForm, contract_name: e.target.value })} placeholder="Enter contract name…" /></div>
              <div className="ct-grid2">
                <div className="ct-field"><div className="k">Effective date</div>
                  <input className="ct-input" type="date" value={editForm.effective_date}
                    onChange={(e) => setEditForm({ ...editForm, effective_date: e.target.value })} /></div>
                <div className="ct-field"><div className="k">Expiration date</div>
                  <input className="ct-input" type="date" value={editForm.expiration_date}
                    onChange={(e) => setEditForm({ ...editForm, expiration_date: e.target.value })} /></div>
              </div>
              <div className="ct-field"><div className="k">Status</div>
                <select className="ct-select" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                </select></div>
            </div>
          </div>
          <div className="ct-mfoot">
            <div className="rt">
              <button className="ct-btn" onClick={() => { setShowEditModal(false); setEditContract(null); }}>Cancel</button>
              <button className="ct-btn pri" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </LinearModal>
      )}

      {/* ── Parse Review Modal (6 sections) ── */}
      {showParseModal && parseData && (
        <LinearModal size="lg"
          title="Review parsed terms"
          subtitle={parseContract ? `${parseContract.contract_number}${parseContract.supplier_name ? ' · ' + parseContract.supplier_name : ''}` : undefined}
          headerRight={confPct != null ? (
            <span className="ct-conf">{confPct}% confidence
              <span className="bar"><i style={{ width: `${confPct}%`, background: confColor }} /></span>
            </span>
          ) : null}
          onClose={() => { setShowParseModal(false); setParseData(null); }}>
          <div className="ct-mbody">

            {/* 1 — Overview */}
            <div className="ct-msec">
              <div className="sh"><span className="st">Overview</span></div>
              <div className="ct-kv">
                <div className="r"><div className="k">Buyer</div><div className="v">{parseData.party_buyer || '-'}</div></div>
                <div className="r"><div className="k">Supplier</div><div className="v">
                  {parseData.party_supplier || '-'}
                  {parseData.matched_supplier_name && <span className="match">✓ matched: {parseData.matched_supplier_name}</span>}
                </div></div>
              </div>
              <div className="ct-kv three" style={{ marginTop: '12px' }}>
                <div className="r"><div className="k">Effective date</div><div className="v">{formatDateFull(parseData.extracted_effective_date)}</div></div>
                <div className="r"><div className="k">Expiration date</div><div className="v">{formatDateFull(parseData.extracted_expiration_date)}</div></div>
                <div className="r"><div className="k">Parse status</div><div className="v" style={{ textTransform: 'capitalize' }}>{parseData.parse_status || '-'}</div></div>
              </div>
              {parseData.parsed_at && (
                <div className="ct-mts">Parsed {formatDateFull(parseData.parsed_at)}{parseData.reviewed_at && ` · Reviewed ${formatDateFull(parseData.reviewed_at)}`}</div>
              )}
            </div>

            {/* 2 — Commercial Terms */}
            <div className="ct-msec">
              <div className="sh"><span className="st">Commercial terms</span></div>
              <div className="ct-kv three">
                <div className="r"><div className="k">Pricing mechanism</div><div className="v">{capitalizeFirst(parseData.pricing_mechanism)}</div></div>
                <div className="r"><div className="k">Payment terms</div><div className="v">{renderSmartField(parseData.payment_terms)}</div></div>
                <div className="r"><div className="k">Price escalation cap</div><div className="v">{parseData.price_escalation_cap ? `${parseData.price_escalation_cap}%` : '-'}</div></div>
                <div className="r"><div className="k">Price review frequency</div><div className="v">{capitalizeFirst(parseData.price_review_frequency)}</div></div>
                <div className="r"><div className="k">Currency</div><div className="v">{parseData.currency ? parseData.currency.toUpperCase() : '-'}</div></div>
                <div className="r"><div className="k">Incoterms</div><div className="v">{parseData.incoterms ? parseData.incoterms.toUpperCase() : '-'}</div></div>
              </div>
              <div style={{ marginTop: '14px' }}>
                <div className="k" style={{ fontSize: '11px', color: L.text3 }}>Volume commitments</div>
                {renderVolumeCommitments(parseData.volume_commitments)}
              </div>
            </div>

            {/* 3 — Renewal Terms */}
            <div className="ct-msec">
              <div className="sh"><span className="st">Renewal terms</span></div>
              <div className="ct-kv four">
                <div className="r"><div className="k">Auto-renewal</div><div className="v">{parseData.auto_renewal == null ? '-' : parseData.auto_renewal ? 'Yes' : 'No'}</div></div>
                <div className="r"><div className="k">Renewal term</div><div className="v">{parseData.renewal_term_months ? `${parseData.renewal_term_months} months` : '-'}</div></div>
                <div className="r"><div className="k">Notice period</div><div className="v">{parseData.renewal_notice_days ? `${parseData.renewal_notice_days} days` : '-'}</div></div>
                <div className="r"><div className="k">Notice deadline <span className="ct-flag">computed</span></div>
                  <div className="v" style={{ color: calcNoticeDeadline() ? L.warn : L.text3 }}>{calcNoticeDeadline() || '-'}</div></div>
              </div>
            </div>

            {/* 4 — Materials */}
            {parseMaterials.length > 0 && (
              <div className="ct-msec">
                <div className="sh"><span className="st">Materials</span>
                  <span className="cnt">{parseMaterials.filter(m => m.matched_material_number).length} of {parseMaterials.length} matched to your catalog</span>
                </div>
                <table className="ct-mtbl">
                  <colgroup>
                    <col style={{ width: '24%' }} /><col style={{ width: '11%' }} /><col style={{ width: '9%' }} />
                    <col style={{ width: '28%' }} /><col style={{ width: '11%' }} /><col style={{ width: '17%' }} />
                  </colgroup>
                  <thead>
                    <tr>{['Description', 'Unit Price', 'Lead Time', 'Matched Material', 'Confidence', 'Method'].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {parseMaterials.map((mat, idx) => {
                      const mp = mat.match_confidence != null ? Math.round(mat.match_confidence * 100) : null;
                      const mc = mp != null ? (mp >= 80 ? L.good : mp >= 50 ? L.warn : L.crit) : L.text3;
                      return (
                        <tr key={mat.parsed_material_id || idx}>
                          <td title={mat.raw_material_description || ''}>{mat.raw_material_description || '-'}</td>
                          <td className="price">{mat.extracted_unit_price ? `$${Number(mat.extracted_unit_price).toFixed(2)}` : '-'}</td>
                          <td>{mat.extracted_lead_time_days ? `${mat.extracted_lead_time_days}d` : '-'}</td>
                          <td style={{ overflow: 'visible' }}>
                            <AsyncSelect
                              value={mat.matched_material_number ? { value: mat.matched_material_number, label: matLabelFor(mat.matched_material_number) } : null}
                              loadOptions={debouncedLoadMaterialOptions}
                              defaultOptions={defaultMatOptions}
                              cacheOptions isClearable
                              placeholder="-- Search for matching material --"
                              onChange={(opt) => updateMaterialMatch(idx, opt?.value || null)}
                              styles={asyncSelectStyles(mat.matched_material_number)}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            />
                          </td>
                          <td>{mp != null ? <span className="cf" style={{ color: mc }}>{mp}%</span> : <span className="cf na">-</span>}</td>
                          <td className="method">{mat.match_method ? mat.match_method.replace(/_/g, ' ') : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* 5 — Key Clauses */}
            <div className="ct-msec">
              <div className="sh"><span className="st">Key clauses</span><span className={`cnt${clausesFound < Object.keys(CLAUSE_LABELS).length ? ' low' : ''}`}>{clausesFound} of {Object.keys(CLAUSE_LABELS).length} found</span></div>
              <div className="ct-clz">
                {Object.entries(CLAUSE_LABELS).map(([key, label]) => {
                  const clause = clauses[key];
                  const hasContent = clause && (clause.summary || clause.is_exclusive != null);
                  const clauseRef = clause?.clause_number || clause?.clause_ref || clause?.section || null;
                  return (
                    <div key={key} className={`c${hasContent ? ' has' : ''}`}>
                      <div className="ctop">
                        <span className="ct-clzt">{label}{clauseRef && <span className="ref">(Clause {clauseRef})</span>}</span>
                        <div className="ct-clzr">
                          {clause?.notice_days && <span className="nd">{clause.notice_days} days notice</span>}
                          {clause?.is_exclusive != null && (
                            <span className={`ct-exbadge ${clause.is_exclusive ? 'yes' : 'no'}`}>{clause.is_exclusive ? 'Exclusive' : 'Non-exclusive'}</span>
                          )}
                        </div>
                      </div>
                      <div className={`ct-clzd${hasContent ? '' : ' absent'}`}>{hasContent ? (clause.summary || '-') : 'Not found in document'}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 6 — Extracted Text (collapsible) */}
            {parseData.extracted_text && (
              <div className="ct-msec">
                <button className={`ct-extxt-h${showExtractedText ? ' open' : ''}`} onClick={() => setShowExtractedText(!showExtractedText)}>
                  <svg className="cv" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
                  Extracted text
                </button>
                {showExtractedText && <div className="ct-extxt-b"><pre>{parseData.extracted_text}</pre></div>}
              </div>
            )}

            {/* AI Notes */}
            {parseData.parse_notes && (
              <div className="ct-msec" style={{ borderBottom: 0 }}>
                <div className="ct-ainote"><b>AI notes:</b> {parseData.parse_notes}</div>
              </div>
            )}
          </div>

          <div className="ct-mfoot">
            <div className="rt">
              <button className="ct-btn" onClick={() => { setShowParseModal(false); setParseData(null); }}>Close</button>
              {parseData.parse_status === 'reviewed' ? (
                <>
                  <span className="ct-accepted">
                    <svg viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5 6.5-7" stroke={L.good} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    Accepted
                  </span>
                  <button className="ct-btn" onClick={() => handleAcceptParsed(true)} disabled={parseReviewing}>{parseReviewing ? 'Applying…' : 'Re-apply to system'}</button>
                </>
              ) : (
                <>
                  <button className="ct-btn" onClick={() => handleAcceptParsed(false)} disabled={parseReviewing}>Save review</button>
                  <button className="ct-btn pri" onClick={() => handleAcceptParsed(true)} disabled={parseReviewing}>{parseReviewing ? 'Applying…' : 'Accept & apply'}</button>
                </>
              )}
            </div>
          </div>
        </LinearModal>
      )}
    </div>
  );
}

export default ManageContracts;
